<template>
  <div class="cart-item">
    <div class="cart-item__top">
      <figure class="cart-item__figure">
        <MenuImage :menu-item="menu" :ad-image-path="menu.ADImagePath" />
      </figure>
      <div class="cart-item__info">
        <p class="cart-item__name">{{ getNameByLocale(menu) }}</p>
        <div class="cart-item__description">{{ optionsNames ? `ㄴ${optionsNames}` : '' }}</div>
      </div>
    </div>

    <div class="cart-item__bottom">
      <div class="cart-item__counter">
        <BaseCounter
          v-model="cartMenu.Qty"
          class="small"
          :disabled="false"
          :min="minQty"
          :limit="limitCount"
          @update:model-value="updateQty"
        />
      </div>

      <div class="cart-item__price">{{ formatCurrency(totalAmountByMenu) }}</div>
    </div>

    <span class="cart-item__remove" @mouseup="handleRemoveClick" @touchend.prevent="handleRemoveClick">
      <img src="@/assets/images/icon-cart-delete.png" alt="장바구니 메뉴 삭제 아이콘" />
    </span>
  </div>
</template>

<script setup lang="ts">
  import BaseCounter from '@/components/base/BaseCounter.vue';
  import MenuImage from '@/components/MenuImage.vue';
  import { useMenu } from '@/composable/useMenu';
  import CartMenu from '@/interfaces/CartMenu';
  import { useCartStore } from '@/stores/cart';
  import { useConfigStore } from '@/stores/config';
  import { useMenuStore } from '@/stores/menu';
  import { formatCurrency, getNameByLocale } from '@/utils';
  import { computed } from 'vue';

  const props = defineProps<{
    /** 장바구니 메뉴 인덱스 */
    index: number;
    /** 장바구니 메뉴 데이터 */
    cartMenu: CartMenu;
  }>();

  const configStore = useConfigStore();
  const menuStore = useMenuStore();
  const cartStore = useCartStore();

  const { calcCartMinOrderQty } = useMenu();

  // 메뉴 데이터
  const menu = computed(() => {
    return menuStore.getMenu(props.cartMenu.GroupCode, props.cartMenu.ItemCode);
  });

  // 최소 주문 수량
  const minQty = computed(() => calcCartMinOrderQty(menu.value));

  // 한정 수량
  const limitCount = computed(() => menuStore.getLimitItem(props.cartMenu.ItemCode)?.OrderLimitCnt);

  // 선택한 토핑/세트메뉴 이름들
  const optionsNames = computed(() => {
    if (props.cartMenu.Toppings.length > 0) {
      return props.cartMenu.Toppings.reduce((acc, option) => {
        const prefix = acc ? `${acc} / ` : '';
        const topping = menuStore.getTopping(option.GroupCode.toString(), option.OptionCode);
        const name = getNameByLocale(topping);
        const price = menuStore.getToppingPrice(topping, menu.value.IsTax2);
        const priceText = price > 0 ? ` (+${price * option.Qty})` : '';
        const qtyText = option.Qty > 1 ? ` x${option.Qty}` : '';

        return `${prefix}${name}${qtyText}${priceText}`;
      }, '');
    } else if (props.cartMenu.SetMenus.length > 0) {
      return props.cartMenu.SetMenus.reduce((acc, option) => {
        const prefix = acc ? `${acc} / ` : '';
        const setMenu = menuStore.getSetMenu(option.MenuItemCode, Number(option.GroupCode), option.OptionCode);
        const name = getNameByLocale(setMenu);
        const price = menuStore.getSetMenuPrice(setMenu, menu.value.IsTax2);
        const qtyText = option.Qty > 1 ? ` x${option.Qty}` : '';
        const priceText = price > 0 ? ` (+${price * option.Qty})` : '';

        return `${prefix}${name}${qtyText}${priceText}`;
      }, '');
    }
    return '';
  });

  // 해당 메뉴의 총 금액
  const totalAmountByMenu = computed(() =>
    cartStore.getCartMenuPrice(props.cartMenu, props.cartMenu.Qty, configStore.config.IsTaxIncluded),
  );

  const handleRemoveClick = () => {
    // 최소 주문 수량 검사

    cartStore.removeCartMenu(props.cartMenu);
  };

  const updateQty = (value: number) => {
    cartStore.setQty(props.index, value);
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .cart-item {
    position: relative;

    .cart-item__top {
      display: flex;
      align-items: center;
    }
    .cart-item__figure {
      display: grid;
      align-items: center;
      width: 4.615vw;
      margin-bottom: 0;
      margin-right: 0.769vw;
      @media (max-width: vars.$lg) {
        width: 70px;
        height: 70px;
        margin-right: 20px;
      }
      @media (max-width: vars.$md) {
        width: 50px;
        height: 50px;
        margin-right: 12px;
      }
      img {
        width: 100%;
        height: 100%;
        object-fit: scale-down;
      }
    }

    .cart-item__info {
      width: calc(100% - 5.385vw);
      @media (max-width: vars.$lg) {
        width: calc(100% - 62px);
      }
    }

    .cart-item__name {
      font-size: 1.6418vw;
      font-weight: 700;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      padding-right: 11.9403vw;
      @media (max-width: vars.$lg) {
        font-size: 20px;
        margin-right: 14px;
      }
      @media (max-width: vars.$md) {
        font-size: 14px;
        margin-right: 14px;
      }
    }
    .cart-item__name + .cart-item__bottom {
      margin-top: 1.6418vw;
      @media (max-width: vars.$lg) {
        margin-top: 16px;
      }
    }
    .cart-item__description {
      font-size: 1.2687vw;
      color: var(--text-secondary);
      margin-top: 0.4478vw;
      padding-right: 11.9403vw;
      text-overflow: ellipsis;
      overflow: hidden;
      display: -webkit-box;
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      @media (max-width: vars.$lg) {
        font-size: 16px;
        padding-right: 30px;
        margin-top: 4px;
      }
      @media (max-width: vars.$md) {
        font-size: 12px;
      }
    }
    .cart-item__description + .cart-item__bottom {
      margin-top: 0.8955vw;
      @media (max-width: vars.$lg) {
        margin-top: 14px;
      }
    }

    .cart-item__bottom {
      margin-top: 0.7463vw;
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      @media (max-width: vars.$lg) {
        margin-top: 16px;
      }
      @media (max-width: vars.$md) {
        margin-top: 12px;
      }
    }

    .cart-item__price {
      font-size: 1.6418vw;
      margin-left: 1.4925vw;
      text-align: right;
      @media (max-width: vars.$lg) {
        font-size: 20px;
        margin-left: 10px;
      }
      @media (max-width: vars.$md) {
        font-size: 14px;
        margin-left: 10px;
      }
    }

    .cart-item__remove {
      position: absolute;
      top: 0;
      right: 0;
      line-height: 1;
      img {
        width: 4.2188vw;
        @media (max-width: vars.$md) {
          width: 28px;
        }
      }
    }
  }
</style>
